import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  CaretLeft, CaretRight, CurrencyDollar, TrendUp, TrendDown,
  Wallet, ChartBar, Plus, Trash, Receipt, CalendarBlank,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";

const MONTH_NAMES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MONTH_FULL = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const EXPENSE_CATEGORIES = [
  { value: "publicidad", label: "Publicidad" },
  { value: "redes_sociales", label: "Redes Sociales" },
  { value: "eventos", label: "Eventos / Expos" },
  { value: "material", label: "Material Impreso" },
  { value: "herramientas", label: "Herramientas Digitales" },
  { value: "contenido", label: "Contenido / Diseño" },
  { value: "otro", label: "Otro" },
];
const fmt = (n) => n?.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00";

export function MarketingBudget({ onBack }) {
  const { token, user } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  const isAdmin = user?.role === "admin";
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, expRes] = await Promise.all([
        axios.get(`${API}/marketing/budget/summary?year=${year}`, { headers }),
        axios.get(`${API}/marketing/expenses?year=${year}`, { headers }),
      ]);
      setSummary(sumRes.data);
      setExpenses(expRes.data);
    } catch { toast.error("Error cargando presupuesto"); }
    setLoading(false);
  }, [token, year]);

  useEffect(() => { load(); }, [load]);

  const chartData = summary?.months?.map(m => ({
    name: MONTH_NAMES[m.month - 1],
    Presupuesto: m.budget,
    Gastado: m.spent,
  })) || [];

  const currentMonthData = summary?.months?.find(m => m.month === selectedMonth);
  const monthExpenses = expenses.filter(e => e.month === selectedMonth);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  return (
    <div className="space-y-5" data-testid="marketing-budget">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="budget-back-btn">
            <CaretLeft size={20} className="text-slate-500" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Presupuesto Marketing</h2>
            <p className="text-sm text-slate-500">Control de ingresos y gastos</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setShowBudgetForm(true)} data-testid="set-budget-btn">
              <Wallet size={16} className="mr-1" /> Asignar Presupuesto
            </Button>
          )}
          <Button size="sm" onClick={() => setShowExpenseForm(true)} data-testid="add-expense-btn">
            <Plus size={16} className="mr-1" /> Registrar Gasto
          </Button>
          <button onClick={() => setYear(y => y - 1)} className="p-1.5 hover:bg-slate-100 rounded-lg"><CaretLeft size={16} /></button>
          <span className="text-lg font-bold text-slate-700 min-w-[50px] text-center">{year}</span>
          <button onClick={() => setYear(y => y + 1)} className="p-1.5 hover:bg-slate-100 rounded-lg"><CaretRight size={16} /></button>
        </div>
      </div>

      {/* KPI Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard icon={Wallet} label="Presupuesto Total" value={`$${fmt(summary.total_budget)}`} color="blue" />
          <KpiCard icon={Receipt} label="Total Gastado" value={`$${fmt(summary.total_spent)}`} color="red" />
          <KpiCard icon={CurrencyDollar} label="Restante" value={`$${fmt(summary.total_remaining)}`}
            color={summary.total_remaining >= 0 ? "green" : "red"} />
          <KpiCard icon={TrendUp} label={`Proyeccion ${MONTH_NAMES[(summary.next_month || 1) - 1]}`}
            value={`$${fmt(summary.next_month_projected_remaining)}`}
            sub={`Pres: $${fmt(summary.next_month_budget)} | Prom gasto: $${fmt(summary.avg_monthly_spend)}`}
            color="purple" />
        </div>
      )}

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Presupuesto vs Gasto por Mes</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => `$${fmt(v)}`} />
              <Legend />
              <Bar dataKey="Presupuesto" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Gastado" fill="#EF4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Monthly detail table */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-slate-50">
          <h3 className="text-sm font-semibold text-slate-700">Detalle Mensual</h3>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-xs" data-testid="budget-monthly-table">
            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="text-left py-2.5 px-3 font-semibold">Mes</th>
                <th className="text-right py-2.5 px-3 font-semibold">Presupuesto</th>
                <th className="text-right py-2.5 px-3 font-semibold">Gastado</th>
                <th className="text-right py-2.5 px-3 font-semibold">Restante</th>
                <th className="text-center py-2.5 px-3 font-semibold">%</th>
                <th className="py-2.5 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {summary?.months?.map(m => {
                const pct = m.budget > 0 ? Math.round((m.spent / m.budget) * 100) : 0;
                const isOver = m.remaining < 0;
                return (
                  <tr key={m.month} className={`border-t hover:bg-slate-50/70 ${selectedMonth === m.month ? "bg-blue-50" : ""}`}>
                    <td className="py-2.5 px-3 font-medium text-slate-700">{MONTH_FULL[m.month - 1]}</td>
                    <td className="py-2.5 px-3 text-right text-slate-600">${fmt(m.budget)}</td>
                    <td className="py-2.5 px-3 text-right text-red-600 font-medium">${fmt(m.spent)}</td>
                    <td className={`py-2.5 px-3 text-right font-semibold ${isOver ? "text-red-600" : "text-emerald-600"}`}>
                      ${fmt(m.remaining)}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      {m.budget > 0 ? (
                        <div className="flex items-center justify-center gap-1">
                          <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${pct > 100 ? "bg-red-500" : pct > 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                              style={{ width: `${Math.min(pct, 100)}%` }} />
                          </div>
                          <span className="text-[10px] text-slate-500">{pct}%</span>
                        </div>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-2.5 px-3">
                      <Button variant="ghost" size="sm" className="text-xs h-6" onClick={() => setSelectedMonth(m.month)}>
                        Ver gastos
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Selected month expenses */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-slate-50 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">
            Gastos de {MONTH_FULL[selectedMonth - 1]} {year}
            {currentMonthData && (
              <span className="ml-2 text-xs font-normal text-slate-500">
                (Presupuesto: ${fmt(currentMonthData.budget)} | Gastado: ${fmt(currentMonthData.spent)} | Restante: ${fmt(currentMonthData.remaining)})
              </span>
            )}
          </h3>
        </div>
        {monthExpenses.length === 0 ? (
          <div className="text-center py-10 text-slate-400">
            <Receipt size={32} className="mx-auto mb-2" />
            <p className="text-sm">Sin gastos registrados en este mes</p>
          </div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-xs" data-testid="budget-expenses-table">
              <thead>
                <tr className="bg-slate-100">
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Concepto</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Categoria</th>
                  <th className="text-right py-2 px-3 font-semibold text-slate-600">Monto</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Registrado por</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Fecha</th>
                  {isAdmin && <th className="py-2 px-3"></th>}
                </tr>
              </thead>
              <tbody>
                {monthExpenses.map(e => (
                  <tr key={e.id} className="border-t hover:bg-slate-50/70">
                    <td className="py-2 px-3 text-slate-700 font-medium">{e.concept}</td>
                    <td className="py-2 px-3">
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        {EXPENSE_CATEGORIES.find(c => c.value === e.category)?.label || e.category}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right text-red-600 font-semibold">${fmt(e.amount)}</td>
                    <td className="py-2 px-3 text-slate-500">{e.created_by_name}</td>
                    <td className="py-2 px-3 text-slate-400">{e.created_at?.slice(0, 10)}</td>
                    {isAdmin && (
                      <td className="py-2 px-3">
                        <button onClick={async () => {
                          if (!window.confirm("Eliminar este gasto?")) return;
                          await axios.delete(`${API}/marketing/expenses/${e.id}`, { headers });
                          toast.success("Gasto eliminado"); load();
                        }} className="text-red-400 hover:text-red-600"><Trash size={14} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Set Budget Dialog */}
      {showBudgetForm && (
        <BudgetFormDialog year={year} onClose={() => { setShowBudgetForm(false); load(); }} />
      )}

      {/* Add Expense Dialog */}
      {showExpenseForm && (
        <ExpenseFormDialog year={year} currentMonth={selectedMonth} onClose={() => { setShowExpenseForm(false); load(); }} />
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color }) {
  const colors = {
    blue: "from-blue-500 to-blue-600", green: "from-emerald-500 to-emerald-600",
    red: "from-red-500 to-red-600", purple: "from-purple-500 to-purple-600",
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color] || colors.blue} rounded-xl p-4 text-white shadow-lg`}>
      <div className="flex items-center gap-2 mb-2 opacity-90">
        <Icon size={18} weight="duotone" />
        <span className="text-[10px] font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-xl font-bold">{value}</p>
      {sub && <p className="text-[10px] opacity-75 mt-1">{sub}</p>}
    </div>
  );
}

function BudgetFormDialog({ year, onClose }) {
  const { token } = useAuth();
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!amount || parseFloat(amount) <= 0) return toast.error("Monto invalido");
    setSaving(true);
    try {
      await axios.put(`${API}/marketing/budget/${year}/${month}`, { initial_budget: parseFloat(amount) },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Presupuesto de ${MONTH_FULL[parseInt(month) - 1]} actualizado`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Asignar Presupuesto Mensual</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Mes</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="mt-1" data-testid="budget-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTH_FULL.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Presupuesto ($)</Label>
            <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="mt-1"
              placeholder="0.00" data-testid="budget-amount-input" />
          </div>
          <p className="text-xs text-slate-500">Ano: {year}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="budget-save-btn">{saving ? "Guardando..." : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExpenseFormDialog({ year, currentMonth, onClose }) {
  const { token } = useAuth();
  const [form, setForm] = useState({
    concept: "", category: "publicidad", amount: "", month: String(currentMonth), year,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const handleSave = async () => {
    if (!form.concept.trim()) return toast.error("Concepto requerido");
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error("Monto invalido");
    setSaving(true);
    try {
      await axios.post(`${API}/marketing/expenses`, {
        ...form, amount: parseFloat(form.amount), month: parseInt(form.month), year,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Gasto registrado");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Registrar Gasto</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Concepto</Label>
            <Input value={form.concept} onChange={e => set("concept", e.target.value)} className="mt-1"
              placeholder="Ej: Campana Facebook Ads" data-testid="expense-concept-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={v => set("category", v)}>
                <SelectTrigger className="mt-1" data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mes</Label>
              <Select value={form.month} onValueChange={v => set("month", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTH_FULL.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Monto ($)</Label>
            <Input type="number" value={form.amount} onChange={e => set("amount", e.target.value)} className="mt-1"
              placeholder="0.00" data-testid="expense-amount-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="expense-save-btn">{saving ? "Guardando..." : "Registrar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
